if (!customElements.get('minimal-catalog-pagination')) {
  customElements.define('minimal-catalog-pagination', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.catalog = this.closest('[data-product-carousel], [data-v3-home-grid]');
      this.fixedGrid = this.catalog?.hasAttribute('data-v3-home-grid');
      this.next = this.querySelector('[data-catalog-next]');
      this.status = this.querySelector('[data-catalog-status]');
      if (!this.catalog || !this.next) return;
      this.next.addEventListener('click', (event) => {
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.loadNext(true);
      }, { signal });
      this.catalog.addEventListener('minimal:catalog-progress', () => this.checkProgress(), { signal });
      window.addEventListener('pagehide', () => this.request?.abort(), { signal });
      window.addEventListener('pageshow', () => this.checkProgress(), { signal });
      this.checkProgress();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
    }

    checkProgress() {
      const stage = this.catalog.dataset.catalogStage === 'true';
      const active = Number(this.catalog.dataset.catalogActiveIndex) || 0;
      const count = this.catalog.querySelectorAll('[data-carousel-slide], [data-v3-grid-card]').length;
      this.classList.toggle('is-stage', stage);
      this.classList.toggle('is-at-end', active >= count - 1);
      if (this.catalog.dataset.catalogReady === 'true' && stage && active >= count - 3 && !this.failed) {
        this.loadNext();
      }
    }

    loadNext(manual = false) {
      if (this.pending) return this.pending;
      if (!this.next.hasAttribute('href') || this.controller.signal.aborted) return Promise.resolve(false);
      this.pending = this.fetchNext(manual).finally(() => {
        this.pending = null;
        if (!this.controller.signal.aborted) this.checkProgress();
      });
      return this.pending;
    }

    async fetchNext(manual) {
      this.failed = false;
      this.request = new AbortController();
      const request = this.request;
      const timeout = window.setTimeout(() => request.abort(), 15000);
      const restoreFocus = manual && document.activeElement === this.next;
      this.setAttribute('aria-busy', 'true');
      this.next.setAttribute('aria-disabled', 'true');
      this.next.textContent = this.dataset.loadingLabel;
      this.status.textContent = '';
      try {
        const url = new URL(this.next.getAttribute('href'), window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Invalid catalog URL');
        url.searchParams.set('section_id', this.dataset.sectionId);
        const currentQuery = new URLSearchParams(window.location.search);
        const preview = currentQuery.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        if (currentQuery.get('catalog') === '1') url.searchParams.set('catalog', '1');
        if (currentQuery.has('oseid')) url.searchParams.set('oseid', currentQuery.get('oseid'));
        const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Catalog request failed');
        const html = await response.text();
        if (request.signal.aborted || !this.isConnected) return false;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const page = parsed.querySelector('minimal-catalog-pagination');
        if (!page || page.dataset.sectionId !== this.dataset.sectionId || page.dataset.collectionId !== this.dataset.collectionId) {
          throw new Error('Unexpected catalog response');
        }
        const currentSlides = Array.from(this.catalog.querySelectorAll('[data-carousel-slide], [data-v3-grid-card]'));
        if (Number(page.dataset.offset) !== Number(this.dataset.offset) + currentSlides.length) {
          throw new Error('Unexpected catalog page');
        }
        const existing = new Set(currentSlides.map((slide) => slide.dataset.catalogProductId));
        const source = page.closest('[data-product-carousel], [data-v3-home-grid]');
        if (!source || source.hasAttribute('data-v3-home-grid') !== this.fixedGrid) throw new Error('Unexpected catalog layout');
        const slides = Array.from(source.querySelectorAll('[data-carousel-slide], [data-v3-grid-card]'));
        const mobile = Array.from(source.querySelectorAll('.product-carousel__mobile-grid-card'));
        const mobileById = new Map(mobile.map((slide) => [slide.dataset.catalogProductId, slide]));
        const newSlides = [];
        const mobileSlides = [];
        slides.forEach((slide) => {
          const id = slide.dataset.catalogProductId;
          if (!id || existing.has(id)) throw new Error('Duplicate catalog product');
          if (!this.fixedGrid && !mobileById.has(id)) throw new Error('Missing mobile product');
          existing.add(id);
          newSlides.push(document.importNode(slide, true));
          if (!this.fixedGrid) mobileSlides.push(document.importNode(mobileById.get(id), true));
        });
        if (!newSlides.length) throw new Error('Empty catalog page');
        const nextHref = page.querySelector('[data-catalog-next]')?.getAttribute('href');
        if (nextHref && new URL(nextHref, url).href === new URL(this.next.getAttribute('href'), url).href) {
          throw new Error('Catalog page did not advance');
        }
        this.catalog.querySelector('[data-carousel-track], [data-v3-grid-track]').append(...newSlides);
        if (!this.fixedGrid) this.catalog.querySelector('.product-carousel__mobile-grid').append(...mobileSlides);
        if (nextHref) this.next.setAttribute('href', nextHref);
        else this.next.removeAttribute('href');
        this.next.hidden = !nextHref;
        this.hidden = !nextHref && !this.querySelector('[data-catalog-previous]');
        this.catalog.dispatchEvent(new CustomEvent('minimal:catalog-appended', { detail: { slides: newSlides, mobileSlides } }));
        if (restoreFocus) {
          const mobileGrid = window.innerWidth < 750 && this.catalog.classList.contains('product-carousel--mobile-grid-active');
          const card = mobileGrid ? mobileSlides[0] : newSlides[0];
          const link = card.querySelector('[data-product-page-link]');
          link?.focus({ preventScroll: true });
          card.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
        return true;
      } catch (error) {
        if (!this.isConnected || this.controller.signal.aborted) return false;
        this.failed = true;
        this.next.textContent = this.dataset.retryLabel;
        this.status.textContent = this.dataset.errorLabel;
        return false;
      } finally {
        window.clearTimeout(timeout);
        this.removeAttribute('aria-busy');
        this.next.removeAttribute('aria-disabled');
        if (!this.failed) this.next.textContent = this.dataset.loadLabel;
      }
    }
  });
}
